import { Component, DestroyRef, inject, input, Input, OnInit, signal, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTree, MatTreeModule } from '@angular/material/tree';
import { CacIconComponent, CacControlBuilderComponent, FormBuilderInputOption, ItemRecord, CacCheckboxComponent, CacButtonComponent } from '@controladad/ng-base';
import { formControl, FormControlExtended } from '@al00x/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, of, startWith, Subscription, take, tap } from 'rxjs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgTemplateOutlet } from '@angular/common';
import { TagStatusValues } from '@admin/shared/data';
import { LocationEntity, TagStatus } from '@admin/core/models';
import { LocationApiService } from '@admin/core/services/location-api.service';
import { convertLocationsToNodes } from '@admin/shared/helpers/utility.helper';

export interface TreeNode {
  id: number;
  label: string;
  subLabel?: string;
  status?: ItemRecord<TagStatus, unknown>;
  children?: TreeNode[];
  disabled?: boolean;
  allChildrenIds?: number[];
}

export interface LocationPath {
  path: string;
  highlighted: boolean;
}

@Component({
  selector: 'app-tree-view',
  imports: [
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    CacIconComponent,
    CacControlBuilderComponent,
    MatProgressBarModule,
    NgTemplateOutlet,
    CacCheckboxComponent,
    CacButtonComponent
  ],
  templateUrl: './tree-view.component.html',
  styleUrl: './tree-view.component.scss',
})
export class TreeViewComponent implements OnInit {
  @ViewChild('tree') tree!: MatTree<TreeNode>;

  @Input() control: FormControlExtended = formControl<number | number[] | undefined>(undefined);

  mode = input<'location' | 'location-tag' | 'external' | 'hidden-tree'>('location');
  fieldMode = input<boolean>(false);
  label = input<string>('');
  multiple = input<boolean>(false);
  readonly = input<boolean>(false);
  externalDataSourse = input<LocationEntity[]>([]);
  highlightedIds = input<number[]>([]);
  searchable = input<boolean>(false);
  disableUnavailableLocations = input<boolean>(false);
  initialSelectedLocationIds = signal<number[] | undefined>(undefined);

  allLocations$ = new BehaviorSubject<LocationEntity[]>([]);
  dataSource = signal<TreeNode[] | null>(null);

  private readonly locationApi = inject(LocationApiService);
  private readonly destroyRef = inject(DestroyRef);

  private searchSub?: Subscription;
  private controlSub?: Subscription;

  childrenAccessor = (node: TreeNode) => node.children ?? [];
  hasChild = (_: number, node: TreeNode) => !!node.children?.length;

  filteredDataSource = signal<TreeNode[] | undefined>(undefined);

  searchControl: FormBuilderInputOption<string> = {
    control: formControl<string>(undefined),
    placeholder: 'جست و جو ...',
  };
  statusControl: FormBuilderInputOption<TagStatus[]> = {
    control: formControl<TagStatus[]>(undefined),
    label: 'وضعیت تگ',
    type: 'select',
    multiple: true,
    items: TagStatusValues,
  };

  locationPaths = signal<LocationPath[]>([]);


  ngOnInit(): void {
    this.getData();
    const value = this.control.value;
    this.initialSelectedLocationIds.set(typeof value === 'number' ? [value] : value);
  }

  onSelectNode(id: number) {
    if (this.readonly()) return;
    this.control.setValue(id);
  }

  onToggleNode(node: TreeNode, checked: boolean) {
    if (this.readonly()) return;
    const value: number[] = this.control.value ?? [];
    let newValue: number[];
    const allIds = node?.allChildrenIds ? [node.id, ...node.allChildrenIds] : [node.id];
    if (checked) {
      newValue = [...new Set([...value, ...allIds])];
    } else {
      newValue = value.filter(x => !allIds.includes(x));
    }
    this.control.setValue(newValue);
  }

  refresh() {
    this.dataSource.set(null);
    this.getData();
  }

  openDialog() {
    dialog$.tree({
      dataSourse: this.allLocations$.value,
      value: this.control.value,
      highlightedIds: this.highlightedIds(),
    });
  }

  private getData() {
    const isModeLocationTag = this.mode() === 'location-tag';
    const dataSource$ =
      (isModeLocationTag ? this.locationApi.getAllLocationTags() : this.mode() === 'location' || this.mode() === 'hidden-tree' ? this.locationApi.getAllLocations() : of(this.externalDataSourse()))
        .pipe(
          tap(locations => {
            this.allLocations$.next(locations);
          }),
          map(x => convertLocationsToNodes(x, isModeLocationTag, this.disableUnavailableLocations(), this.initialSelectedLocationIds()))
        );
    dataSource$.pipe(take(1)).subscribe((res) => {
      this.dataSource.set(res);
      if (res && this.searchable()) {
        this.initSearch(res);
      }
      setTimeout(() => {
        if (this.fieldMode()) {
          this.listenToControl();
          this.expandNodeById(res ?? [], this.control.value);
        } else {
          this.tree?.expandAll();
        }
      }, 10);
    });
  }

  private listenToControl() {
    this.controlSub?.unsubscribe();
    this.controlSub = this.control.valueChanges.pipe(
      startWith(this.control.value),
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged()
    ).subscribe((value) => {
      const data = this.dataSource();
      if (data) {
        this.updateLocationPaths(data, value);
      }
    });
  }

  private initSearch(nodes: TreeNode[]) {
    this.searchSub?.unsubscribe();
    this.searchSub = this.searchControl.control.value$
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged(), debounceTime(300))
      .subscribe((value) => {
        this.filteredDataSource.set(this.filterNodes(nodes, value));
        this.syncOnSearchChange(value);
      });
    this.statusControl.control.value$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.filteredDataSource.set(this.filterStatus(nodes, value));
        this.syncOnSearchChange(value);
      });
  }

  private filterNodes(nodes: TreeNode[], searchTerm?: string) {
    const value = searchTerm?.trim()?.toLowerCase();
    if (!value) return undefined;

    const result: TreeNode[] = [];
    for (const node of nodes) {
      const matches = node.label.toLowerCase().includes(value);
      const filteredChildren = node.children?.length ? this.filterNodes(node.children, searchTerm) : [];
      if (matches || filteredChildren?.length) {
        result.push({
          ...node,
          children: filteredChildren?.length ? filteredChildren : [],
        });
      }
    }
    return result;
  }

  private filterStatus(nodes: TreeNode[], value?: TagStatus[]) {
    if (!value?.length) return undefined;

    const result: TreeNode[] = [];
    for (const node of nodes) {
      const nodeStatus = node.status?.value;
      const matches = nodeStatus !== undefined && value.includes(nodeStatus);
      const filteredChildren = node.children?.length ? this.filterStatus(node.children, value) : [];
      if (matches || filteredChildren?.length) {
        result.push({
          ...node,
          children: filteredChildren?.length ? filteredChildren : [],
        });
      }
    }
    return result;
  }

  private expandNodeById(nodes: TreeNode[], value: number | number[]): boolean {
    if (!value || !this.tree) return false;
    const ids = typeof value === 'number' ? [value] : value;
    let foundAny = false;
    for (const node of nodes) {
      if (ids.includes(node.id)) {
        this.tree.expand(node);
        foundAny = true;
      }
      if (node.children && node.children.length > 0) {
        const foundInChildren = this.expandNodeById(node.children, value);
        if (foundInChildren) {
          this.tree.expand(node);
          foundAny = true;
        }
      }
    }
    return foundAny;
  }

  private updateLocationPaths(nodes: TreeNode[], value: number | number[] | undefined) {
    if (!value || !nodes) {
      this.locationPaths.set([]);
      return;
    }
    const ids = typeof value === 'number' ? [value] : value;
    const sortedIds = [...ids].sort((a, b) => b - a);
    const newPaths: LocationPath[] = [];
    sortedIds.forEach(id => {
      const node = this.findPathToNode(nodes, id);
      if (node && node.length > 0) {
        newPaths.push({ path: node.map(x => x.label).join('  ⟵  '), highlighted: this.highlightedIds().includes(id) })
      }
    });
    this.locationPaths.set(newPaths);
  }

  private findPathToNode(nodes: TreeNode[], targetId: number, currentPath: TreeNode[] = []): TreeNode[] | null {
    for (const node of nodes) {
      const newPath = [...currentPath, node];
      if (node.id === targetId) {
        return newPath;
      }

      if (node.children && node.children.length > 0) {
        const found = this.findPathToNode(node.children, targetId, newPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private syncOnSearchChange(value: TagStatus[] | string | undefined | null) {
    const isFielsMode = this.fieldMode();
    if (value !== null && !isFielsMode) {
      this.control.setValue(undefined);
    }
    setTimeout(() => {
      if (value) {
        this.tree?.expandAll();
      } else if (isFielsMode) {
        this.tree.collapseAll();
        this.expandNodeById(this.dataSource() ?? [], this.control.value);
      }
    }, 10);
  }

}
